import {
  Route,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { MissionKindEnum, PermissionEnum } from '@e-socna/data-layer';

import { LoggedInLayout } from '../layouts/LoggedIn';
import { LoggedOutLayout } from '../layouts/LoggedOut';
import { HomePage } from '../pages/Home';
import { GisPage } from '../pages/Gis';
import { FoldersPage } from '../pages/Folders';
import { NewFolderPage } from '../pages/NewFolder';
import { FolderPage } from '../pages/Folder';
import { FolderBusinessEventsPage } from '../pages/FolderBusinessEvents';
import { FolderFilesPage } from '../pages/FolderFiles';
import { FolderMissionsPage } from '../pages/FolderMissions';
import { CollaborationMissionsPage } from '../pages/CollaborationMissions';
import { NewMissionPage } from '../pages/NewMission';
import { MissionPage } from '../pages/Mission';
import { MissionQuotationPage } from '../pages/MissionQuotation';
import { MissionServiceUsagesPage } from '../pages/MissionServiceUsages';
import { MissionReportPage } from '../pages/MissionReport';
import { MissionReportsPage } from '../pages/MissionReports';
import { MissionReportInitPage } from '../pages/MissionReportInit';
import { MissionToolkitsPage } from '../pages/MissionToolkits';
import { MissionAccountingPage } from '../pages/MissionAccounting';
import { AccountingContextProvider } from '../contexts/AccountingContext';
import { MissionBusinessEventsPage } from '../pages/MissionBusinessEvents';
import { MissionFilesPage } from '../pages/MissionFiles';
import { BusinessEventPage } from '../pages/BusinessEvent';
import { ExternalThirdPartiesPage } from '../pages/ThirdParties/Externals';
import { NewExternalOrganizationPage } from '../pages/NewOrganization/External';
import { OrganizationSearchPage } from '../pages/OrganizationSearch';
import { NewExternalPersonPage } from '../pages/NewPerson/External';
import { InternalThirdPartiesPage } from '../pages/ThirdParties/Internals';
import { NewInternalOrganizationPage } from '../pages/NewOrganization/Internal';
import { NewInternalPersonPage } from '../pages/NewPerson/Internal';
import { OrganizationPage } from '../pages/Organization';
import { PersonPage } from '../pages/Person';
import { ReferencesPage } from '../pages/References';
import { WorkflowsPage } from '../pages/Workflows';
import { DataPage } from '../pages/Data';
import { UsersPage } from '../pages/Users';
import { NewUserPage } from '../pages/NewUser';
import { LocalityInfosPage } from '../pages/LocalityInfos';
import { UserPage } from '../pages/User';
import { MyAccountPage } from '../pages/MyAccount';
import { CostsPage } from '../pages/Costs';
import { CostPage } from '../pages/Cost';
import { NewCostPage } from '../pages/NewCost';
import { MaterialsPage } from '../pages/Materials';
import { MaterialPage } from '../pages/Material';
import { NewMaterialPage } from '../pages/NewMaterial';
import { ResolvableServicePage } from '../pages/ResolvableService';
import { ResolvableServicesPage } from '../pages/ResolvableServices';
import { NewResolvableServicePage } from '../pages/NewResolvableService';
import { ResolvableMissionTypePage } from '../pages/ResolvableMissionType';
import { ResolvableMissionTypesPage } from '../pages/ResolvableMissionTypes';
import { NewResolvableMissionTypePage } from '../pages/NewResolvableMissionType';
import { DocumentAndSectionTemplatesPage } from '../pages/DocumentAndSectionTemplates';
import { NewDocumentTemplatePage } from '../pages/NewDocumentTemplate';
import { DocumentTemplatePage } from '../pages/DocumentTemplate';
import { NewSectionTemplatePage } from '../pages/NewSectionTemplate';
import { SectionTemplatePage } from '../pages/SectionTemplate';
import { LoginPage } from '../pages/Login';
import { HealthCheckPage } from '../pages/HealthCheck';
import { NotFoundPage } from '../pages/NotFound';
import { RequirePermission } from '../components/RequirePermission';
import {
  FolderContextProvider,
  DummyFolderContextProvider,
} from '../contexts/FolderContext';
import {
  MissionContextProvider,
  DummyMissionContextProvider,
} from '../contexts/MissionContext';
import { QuotationContextProvider } from '../contexts/QuotationContext';
import { ReportContextProvider } from '../contexts/ReportContext';
import { BusinessEventContextProvider } from '../contexts/BusinessEventContext';
import { Page } from '../contexts/NavigationContext/enums/page.enum';

import { withSubMissionRoutes } from './utils/sub-mission-routes.util';
import * as PATHS from './constants/paths.constant';

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <>
      <Route element={<LoggedInLayout />}>
        <Route element={<HomePage />} index />

        {/* ============================================ */}
        {/* GIS Routes                                  */}
        {/* ============================================ */}
        <Route element={<GisPage />} path={PATHS.PATH_GIS_DEFAULT} />
        <Route element={<GisPage />} path={PATHS.PATH_GIS} />
        <Route element={<GisPage />} path={PATHS.PATH_GIS_MAP_SETTINGS} />
        <Route
          element={<GisPage />}
          path={PATHS.PATH_GIS_FEATURE_DETAILS_CALCULATION_ASSUMPTIONS}
        />
        <Route
          element={<GisPage />}
          path={PATHS.PATH_GIS_FEATURE_DETAILS_DEFAULT}
        />
        <Route
          element={<GisPage />}
          path={PATHS.PATH_GIS_FEATURE_DETAILS_DISTANCES}
        />
        <Route
          element={<GisPage />}
          path={PATHS.PATH_GIS_FEATURE_DETAILS_FOLDER_BUSINESS_ENTITY}
        />
        <Route
          element={<GisPage />}
          path={PATHS.PATH_GIS_FEATURE_DETAILS_GEOCODING}
        />
        <Route
          element={<GisPage />}
          path={PATHS.PATH_GIS_FEATURE_DETAILS_NATURAL_DISASTERS}
        />
        <Route
          element={<GisPage />}
          path={PATHS.PATH_GIS_FEATURE_DETAILS_OFFICIAL_JOURNAL}
        />
        <Route
          element={<GisPage />}
          path={PATHS.PATH_GIS_FEATURE_DETAILS_THIRD_PARTY}
        />

        {/* ============================================ */}
        {/* Folders List (no context needed)            */}
        {/* ============================================ */}
        <Route element={<FoldersPage />} path={PATHS.PATH_FOLDERS_DEFAULT} />
        <Route element={<NewFolderPage />} path={PATHS.PATH_FOLDERS_NEW} />
        <Route element={<FoldersPage />} path={PATHS.PATH_FOLDERS_OPEN} />
        <Route element={<FoldersPage />} path={PATHS.PATH_FOLDERS_NOT_OPEN} />

        {/* ============================================ */}
        {/* FOLDER ROUTES - Wrapped in FolderContext    */}
        {/* All folder and nested mission routes        */}
        {/* ============================================ */}

        {/* FolderPage routes */}
        <Route element={<FolderContextProvider page={Page.Folder} />}>
          <Route element={<FolderPage />} path={PATHS.PATH_FOLDER_GENERAL} />
          <Route element={<FolderPage />} path={PATHS.PATH_FOLDER_DEFAULT} />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_GENERAL_EDIT}
          />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_GENERAL_EDIT_CODE}
          />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_GENERAL_EDIT_STATE}
          />
          <Route element={<FolderPage />} path={PATHS.PATH_FOLDER_ADDRESS} />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_ADDRESS_EDIT}
          />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_INTERNAL_ACTORS}
          />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_INTERNAL_ACTORS_EDIT}
          />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_INTERNAL_ACTORS_EDIT_DELETE}
          />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_INTERNAL_ACTORS_EDIT_FLUSH}
          />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_INTERNAL_ACTORS_EDIT_CUSTOMIZE}
          />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_INTERNAL_ACTORS_EDIT_MODIFY}
          />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_INTERNAL_ACTORS_EDIT_NEW}
          />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_EXTERNAL_ACTORS}
          />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_EXTERNAL_ACTORS_EDIT}
          />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_EXTERNAL_ACTORS_EDIT_DELETE}
          />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_EXTERNAL_ACTORS_EDIT_FLUSH}
          />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_EXTERNAL_ACTORS_EDIT_CUSTOMIZE}
          />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_EXTERNAL_ACTORS_EDIT_MODIFY}
          />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_EXTERNAL_ACTORS_EDIT_NEW}
          />
          <Route
            element={<FolderPage />}
            path={PATHS.PATH_FOLDER_ARCHIVE_INFO}
          />
          <Route element={<FolderPage />} path={PATHS.PATH_FOLDER_VERSIONS} />
        </Route>

        {/* FolderMissionsPage routes */}
        <Route element={<FolderContextProvider page={Page.FolderMissions} />}>
          <Route
            element={<FolderMissionsPage />}
            path={PATHS.PATH_FOLDER_MISSIONS}
          />
          <Route
            element={<NewMissionPage />}
            path={PATHS.PATH_FOLDER_MISSIONS_NEW}
          />
          <Route
            element={<NewMissionPage kind={MissionKindEnum.Composite} />}
            path={PATHS.PATH_FOLDER_COLLABORATIONS_NEW}
          />
        </Route>

        {/* FolderFilesPage routes */}
        <Route element={<FolderContextProvider page={Page.FolderFiles} />}>
          <Route
            element={<FolderFilesPage />}
            path={PATHS.PATH_FOLDER_FILES_DEFAULT}
          />
          <Route
            element={<FolderFilesPage />}
            path={PATHS.PATH_FOLDER_FILES_DETAIL}
          />
        </Route>

        {/* FolderBusinessEventsPage routes */}
        <Route
          element={<FolderContextProvider page={Page.FolderBusinessEvents} />}
        >
          <Route element={<BusinessEventContextProvider scope="folder" />}>
            <Route
              element={<FolderBusinessEventsPage />}
              path={PATHS.PATH_FOLDER_BUSINESS_EVENTS}
            />
          </Route>
        </Route>

        {/* ============================================ */}
        {/* MISSION ROUTES - Nested under FolderContext */}
        {/* FolderContext -> MissionContext             */}
        {/* ============================================ */}

        {/* MissionPage routes */}
        <Route element={<FolderContextProvider page={Page.Mission} />}>
          {withSubMissionRoutes(
            <Route element={<MissionContextProvider />}>
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_GENERAL}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_DEFAULT}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_GENERAL_EDIT}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_GENERAL_EDIT_CODE}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_GENERAL_EDIT_COLLABORATION}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_GENERAL_EDIT_STATE}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_ADDRESS}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_ADDRESS_EDIT}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_INTERNAL_ACTORS}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_INTERNAL_ACTORS_EDIT}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_INTERNAL_ACTORS_EDIT_DELETE}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_INTERNAL_ACTORS_EDIT_FLUSH}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_INTERNAL_ACTORS_EDIT_CUSTOMIZE}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_INTERNAL_ACTORS_EDIT_MODIFY}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_INTERNAL_ACTORS_EDIT_NEW}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_EXTERNAL_ACTORS}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_EXTERNAL_ACTORS_EDIT}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_EXTERNAL_ACTORS_EDIT_DELETE}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_EXTERNAL_ACTORS_EDIT_FLUSH}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_EXTERNAL_ACTORS_EDIT_CUSTOMIZE}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_EXTERNAL_ACTORS_EDIT_MODIFY}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_EXTERNAL_ACTORS_EDIT_NEW}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_ARCHIVE_INFO}
              />
              <Route
                element={<MissionPage />}
                path={PATHS.PATH_MISSION_VERSIONS}
              />
            </Route>,
          )}
        </Route>

        {/* MissionToolkitsPage routes */}
        <Route element={<FolderContextProvider page={Page.MissionToolkits} />}>
          {withSubMissionRoutes(
            <Route element={<MissionContextProvider />}>
              <Route
                element={<MissionToolkitsPage />}
                path={PATHS.PATH_MISSION_TOOLKITS}
              />
              <Route
                element={<MissionToolkitsPage />}
                path={PATHS.PATH_MISSION_TOOLKITS_DEFAULT}
              />
              <Route
                element={<MissionToolkitsPage />}
                path={PATHS.PATH_MISSION_TOOLKIT}
              />
              <Route
                element={<MissionToolkitsPage />}
                path={PATHS.PATH_MISSION_TOOLKIT_EDIT}
              />
            </Route>,
          )}
        </Route>

        {/* ===================================================== */}
        {/* ACCOUNTING ROUTES                                     */}
        {/* FolderContext -> MissionContext -> AccountingContext  */}
        {/* ===================================================== */}
        <Route
          element={<FolderContextProvider page={Page.MissionAccounting} />}
        >
          {withSubMissionRoutes(
            <Route element={<MissionContextProvider />}>
              <Route element={<AccountingContextProvider />}>
                <Route
                  element={<MissionAccountingPage />}
                  path={PATHS.PATH_MISSION_ACCOUNTING_INFO}
                />
                <Route
                  element={<MissionAccountingPage />}
                  path={PATHS.PATH_MISSION_ACCOUNTING_DEFAULT}
                />
                <Route
                  element={<MissionAccountingPage />}
                  path={PATHS.PATH_MISSION_ACCOUNTING_INIT}
                />
              </Route>
            </Route>,
          )}
        </Route>

        {/* MissionFilesPage routes */}
        <Route element={<FolderContextProvider page={Page.MissionFiles} />}>
          {withSubMissionRoutes(
            <Route element={<MissionContextProvider />}>
              <Route
                element={<MissionFilesPage />}
                path={PATHS.PATH_MISSION_FILES_DEFAULT}
              />
              <Route
                element={<MissionFilesPage />}
                path={PATHS.PATH_MISSION_FILES_DETAIL}
              />
            </Route>,
          )}
        </Route>

        {/* MissionBusinessEventsPage routes */}
        <Route
          element={<FolderContextProvider page={Page.MissionBusinessEvents} />}
        >
          {withSubMissionRoutes(
            <Route element={<MissionContextProvider />}>
              <Route element={<BusinessEventContextProvider scope="mission" />}>
                <Route
                  element={<MissionBusinessEventsPage />}
                  path={PATHS.PATH_MISSION_BUSINESS_EVENTS}
                />
              </Route>
            </Route>,
          )}
        </Route>

        {/* ==================================================== */}
        {/* QUOTATION ROUTES                                     */}
        {/* FolderContext -> MissionContext -> QuotationContext  */}
        {/* ==================================================== */}
        <Route element={<FolderContextProvider page={Page.MissionQuotation} />}>
          {withSubMissionRoutes(
            <Route element={<MissionContextProvider />}>
              <Route element={<QuotationContextProvider />}>
                <Route
                  element={<MissionQuotationPage />}
                  path={PATHS.PATH_MISSION_QUOTATION_INFO}
                />
                <Route
                  element={<MissionQuotationPage />}
                  path={PATHS.PATH_MISSION_QUOTATION_INFO_EDIT}
                />
                <Route
                  element={<MissionQuotationPage />}
                  path={PATHS.PATH_MISSION_QUOTATION_INFO_EDIT_STATE}
                />
                <Route
                  element={<MissionQuotationPage />}
                  path={PATHS.PATH_MISSION_QUOTATION_INIT}
                />
                <Route
                  element={<MissionQuotationPage />}
                  path={PATHS.PATH_MISSION_QUOTATION_EDITOR}
                />
                <Route
                  element={<MissionQuotationPage />}
                  path={PATHS.PATH_MISSION_QUOTATION_DEFAULT}
                />
                <Route
                  element={<MissionQuotationPage />}
                  path={PATHS.PATH_MISSION_QUOTATION_SUMMARY}
                />
                <Route
                  element={<MissionQuotationPage />}
                  path={PATHS.PATH_MISSION_QUOTATION_PREVIEW}
                />
                <Route
                  element={<MissionQuotationPage />}
                  path={PATHS.PATH_MISSION_QUOTATION_GENERATION}
                />
                <Route
                  element={<MissionQuotationPage />}
                  path={PATHS.PATH_MISSION_QUOTATION_SHARE}
                />
                <Route
                  element={<MissionQuotationPage />}
                  path={PATHS.PATH_MISSION_QUOTATION_VERSIONS}
                />
              </Route>
            </Route>,
          )}
        </Route>

        {/* ==================================================== */}
        {/* COLLABORATION (COMPOSITE MISSION) MISSIONS ROUTES    */}
        {/* FolderContext -> MissionContext                     */}
        {/* ==================================================== */}
        <Route
          element={<FolderContextProvider page={Page.MissionSubMissions} />}
        >
          {withSubMissionRoutes(
            <Route element={<MissionContextProvider />}>
              <Route
                element={<CollaborationMissionsPage />}
                path={PATHS.PATH_MISSION_SUB_MISSIONS}
              />
              {/* Created from its collaboration: the menu keeps its space. */}
              <Route
                element={<NewMissionPage kind={MissionKindEnum.SubMission} />}
                path={PATHS.PATH_COLLABORATION_MISSIONS_NEW}
              />
            </Route>,
          )}
        </Route>

        {/* ==================================================== */}
        {/* SERVICE USAGES (CHIFFRAGE) ROUTES                    */}
        {/* FolderContext -> MissionContext                     */}
        {/* ==================================================== */}
        <Route
          element={<FolderContextProvider page={Page.MissionServiceUsages} />}
        >
          {withSubMissionRoutes(
            <Route element={<MissionContextProvider />}>
              <Route
                element={<MissionServiceUsagesPage />}
                path={PATHS.PATH_MISSION_SERVICE_USAGES}
              />
              <Route
                element={<MissionServiceUsagesPage />}
                path={PATHS.PATH_MISSION_SERVICE_USAGES_SUB_MISSION}
              />
              <Route
                element={<MissionServiceUsagesPage />}
                path={PATHS.PATH_MISSION_SERVICE_USAGES_EDIT}
              />
              <Route
                element={<MissionServiceUsagesPage />}
                path={PATHS.PATH_MISSION_SERVICE_USAGES_EDIT_SUB_MISSION}
              />
              <Route
                element={<MissionServiceUsagesPage />}
                path={PATHS.PATH_MISSION_SERVICE_USAGES_EDIT_DELETE}
              />
              <Route
                element={<MissionServiceUsagesPage />}
                path={PATHS.PATH_MISSION_SERVICE_USAGES_EDIT_FLUSH_ALL}
              />
              <Route
                element={<MissionServiceUsagesPage />}
                path={PATHS.PATH_MISSION_SERVICE_USAGES_EDIT_FLUSH_ONE}
              />
              <Route
                element={<MissionServiceUsagesPage />}
                path={PATHS.PATH_MISSION_SERVICE_USAGES_EDIT_CUSTOMIZE_ALL}
              />
              <Route
                element={<MissionServiceUsagesPage />}
                path={PATHS.PATH_MISSION_SERVICE_USAGES_EDIT_CUSTOMIZE_ONE}
              />
              <Route
                element={<MissionServiceUsagesPage />}
                path={PATHS.PATH_MISSION_SERVICE_USAGES_EDIT_MODIFY}
              />
              <Route
                element={<MissionServiceUsagesPage />}
                path={PATHS.PATH_MISSION_SERVICE_USAGES_EDIT_NEW}
              />
              <Route
                element={<MissionServiceUsagesPage />}
                path={PATHS.PATH_MISSION_SERVICE_USAGES_EDIT_NEW_IN_GROUP}
              />
              <Route
                element={<MissionServiceUsagesPage />}
                path={PATHS.PATH_MISSION_SERVICE_USAGES_EDIT_REORDER}
              />
            </Route>,
          )}
        </Route>

        {/* ================================================ */}
        {/* REPORTS LIST ROUTES                              */}
        {/* FolderContext -> MissionContext                  */}
        {/* ================================================ */}
        <Route element={<FolderContextProvider page={Page.MissionReport} />}>
          {withSubMissionRoutes(
            <Route element={<MissionContextProvider />}>
              <Route
                element={<MissionReportsPage />}
                path={PATHS.PATH_MISSION_REPORTS}
              />
              <Route
                element={<MissionReportInitPage />}
                path={PATHS.PATH_MISSION_REPORTS_INIT}
              />
            </Route>,
          )}
        </Route>

        {/* ================================================ */}
        {/* ONE REPORT ROUTES                                */}
        {/* FolderContext -> MissionContext -> ReportContext */}
        {/* ================================================ */}
        <Route element={<FolderContextProvider page={Page.MissionReport} />}>
          {withSubMissionRoutes(
            <Route element={<MissionContextProvider />}>
              <Route element={<ReportContextProvider />}>
                <Route
                  element={<MissionReportPage />}
                  path={PATHS.PATH_MISSION_REPORT_INFO}
                />
                <Route
                  element={<MissionReportPage />}
                  path={PATHS.PATH_MISSION_REPORT_INFO_EDIT}
                />
                <Route
                  element={<MissionReportPage />}
                  path={PATHS.PATH_MISSION_REPORT_INFO_EDIT_STATE}
                />
                <Route
                  element={<MissionReportPage />}
                  path={PATHS.PATH_MISSION_REPORT_EDITOR}
                />
                <Route
                  element={<MissionReportPage />}
                  path={PATHS.PATH_MISSION_REPORT_DEFAULT}
                />
                <Route
                  element={<MissionReportPage />}
                  path={PATHS.PATH_MISSION_REPORT_SUMMARY}
                />
                <Route
                  element={<MissionReportPage />}
                  path={PATHS.PATH_MISSION_REPORT_PREVIEW}
                />
                <Route
                  element={<MissionReportPage />}
                  path={PATHS.PATH_MISSION_REPORT_GENERATION}
                />
                <Route
                  element={<MissionReportPage />}
                  path={PATHS.PATH_MISSION_REPORT_SHARE}
                />
                <Route
                  element={<MissionReportPage />}
                  path={PATHS.PATH_MISSION_REPORT_VERSIONS}
                />
              </Route>
            </Route>,
          )}
        </Route>

        {/* ============================================ */}
        {/* Business Event Page (standalone)             */}
        {/* ============================================ */}
        <Route
          element={<BusinessEventPage />}
          path={PATHS.PATH_BUSINESS_EVENT_GENERAL}
        />
        <Route
          element={<BusinessEventPage />}
          path={PATHS.PATH_BUSINESS_EVENT_DEFAULT}
        />

        {/* ============================================ */}
        {/* Third Parties                                */}
        {/* ============================================ */}
        <Route
          element={<ExternalThirdPartiesPage />}
          path={PATHS.PATH_EXTERNAL_THIRD_PARTIES_DEFAULT}
        />
        <Route
          element={<ExternalThirdPartiesPage />}
          path={PATHS.PATH_EXTERNAL_ORGANIZATIONS}
        />
        <Route
          element={<NewExternalOrganizationPage />}
          path={PATHS.PATH_EXTERNAL_ORGANIZATIONS_NEW}
        />
        <Route
          element={<OrganizationSearchPage />}
          path={PATHS.PATH_EXTERNAL_ORGANIZATIONS_SEARCH}
        />
        <Route
          element={<ExternalThirdPartiesPage />}
          path={PATHS.PATH_EXTERNAL_PEOPLE}
        />
        <Route
          element={<NewExternalPersonPage />}
          path={PATHS.PATH_EXTERNAL_PEOPLE_NEW}
        />
        <Route
          element={<ExternalThirdPartiesPage />}
          path={PATHS.PATH_EXTERNAL_THIRD_PARTIES_DISABLED}
        />
        <Route
          element={
            <RequirePermission
              permission={PermissionEnum.InternalThirdPartiesWrite}
            />
          }
        >
          <Route
            element={<InternalThirdPartiesPage />}
            path={PATHS.PATH_INTERNAL_THIRD_PARTIES_DEFAULT}
          />
          <Route
            element={<InternalThirdPartiesPage />}
            path={PATHS.PATH_INTERNAL_ORGANIZATIONS}
          />
          <Route
            element={<NewInternalOrganizationPage />}
            path={PATHS.PATH_INTERNAL_ORGANIZATIONS_NEW}
          />
          <Route
            element={<InternalThirdPartiesPage />}
            path={PATHS.PATH_INTERNAL_PEOPLE}
          />
          <Route
            element={<NewInternalPersonPage />}
            path={PATHS.PATH_INTERNAL_PEOPLE_NEW}
          />
          <Route
            element={<InternalThirdPartiesPage />}
            path={PATHS.PATH_INTERNAL_THIRD_PARTIES_DISABLED}
          />
        </Route>

        {/* ============================================ */}
        {/* Admin & Reference Pages                      */}
        {/* ============================================ */}
        <Route element={<ReferencesPage />} path={PATHS.PATH_REFERENCES} />
        <Route element={<WorkflowsPage />} path={PATHS.PATH_WORKFLOWS} />
        <Route
          element={
            <RequirePermission permission={PermissionEnum.FixturesManage} />
          }
        >
          <Route element={<DataPage />} path={PATHS.PATH_DATA} />
        </Route>
        <Route
          element={
            <RequirePermission permission={PermissionEnum.UsersManage} />
          }
        >
          <Route element={<UsersPage />} path={PATHS.PATH_USERS_DEFAULT} />
          <Route element={<UsersPage />} path={PATHS.PATH_USERS_INTERNAL} />
          <Route element={<UsersPage />} path={PATHS.PATH_USERS_EXTERNAL} />
          <Route element={<UsersPage />} path={PATHS.PATH_USERS_DISABLED} />
          <Route
            element={<NewUserPage isInternal />}
            path={PATHS.PATH_USERS_NEW_INTERNAL}
          />
          <Route
            element={<NewUserPage isInternal={false} />}
            path={PATHS.PATH_USERS_NEW_EXTERNAL}
          />
        </Route>
        <Route
          element={<LocalityInfosPage />}
          path={PATHS.PATH_LOCALITY_INFOS}
        />

        {/* ============================================ */}
        {/* Person Pages                                 */}
        {/* ============================================ */}
        <Route element={<PersonPage />} path={PATHS.PATH_PERSON_DEFAULT} />
        <Route element={<PersonPage />} path={PATHS.PATH_PERSON_GENERAL} />
        <Route element={<PersonPage />} path={PATHS.PATH_PERSON_GENERAL_EDIT} />
        <Route element={<PersonPage />} path={PATHS.PATH_PERSON_CONTACT} />
        <Route element={<PersonPage />} path={PATHS.PATH_PERSON_CONTACT_EDIT} />
        <Route element={<PersonPage />} path={PATHS.PATH_PERSON_ADDRESSES} />
        <Route
          element={<PersonPage />}
          path={PATHS.PATH_PERSON_ADDRESSES_EDIT}
        />
        <Route
          element={<PersonPage />}
          path={PATHS.PATH_PERSON_ADDRESSES_EDIT_NEW}
        />
        <Route
          element={<PersonPage />}
          path={PATHS.PATH_PERSON_ADDRESSES_EDIT_MODIFY}
        />
        <Route
          element={<PersonPage />}
          path={PATHS.PATH_PERSON_ADDRESSES_EDIT_DELETE}
        />
        <Route element={<PersonPage />} path={PATHS.PATH_PERSON_BILLING} />
        <Route element={<PersonPage />} path={PATHS.PATH_PERSON_BILLING_EDIT} />
        <Route element={<PersonPage />} path={PATHS.PATH_PERSON_BANK} />
        <Route element={<PersonPage />} path={PATHS.PATH_PERSON_BANK_EDIT} />
        <Route element={<PersonPage />} path={PATHS.PATH_PERSON_MEMBERSHIPS} />
        <Route
          element={<PersonPage />}
          path={PATHS.PATH_PERSON_MEMBERSHIPS_EDIT}
        />
        <Route
          element={<PersonPage />}
          path={PATHS.PATH_PERSON_PARTICIPATIONS}
        />
        <Route element={<PersonPage />} path={PATHS.PATH_PERSON_VERSIONS} />
        <Route element={<PersonPage />} path={PATHS.PATH_PERSON_ADMIN} />
        <Route element={<PersonPage />} path={PATHS.PATH_PERSON_ADMIN_EDIT} />
        <Route element={<PersonPage />} path={PATHS.PATH_PERSON_PENNYLANE} />

        {/* ============================================ */}
        {/* Organization Pages                           */}
        {/* ============================================ */}
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_DEFAULT}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_GENERAL}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_GENERAL_EDIT}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_ADDRESSES}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_ADDRESSES_EDIT}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_ADDRESSES_EDIT_NEW}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_ADDRESSES_EDIT_MODIFY}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_ADDRESSES_EDIT_DELETE}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_BILLING}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_BILLING_EDIT}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_RELATIONS}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_RELATIONS_EDIT}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_BANK}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_BANK_EDIT}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_MEMBERSHIPS}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_MEMBERSHIPS_EDIT}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_CONTACT}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_CONTACT_EDIT}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_PARTICIPATIONS}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_DIRECTORY}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_VERSIONS}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_ADMIN}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_ADMIN_EDIT}
        />
        <Route
          element={<OrganizationPage />}
          path={PATHS.PATH_ORGANIZATION_PENNYLANE}
        />

        {/* ============================================ */}
        {/* User Pages                                   */}
        {/* ============================================ */}
        <Route
          element={
            <RequirePermission permission={PermissionEnum.UsersManage} />
          }
        >
          <Route element={<UserPage />} path={PATHS.PATH_USER_DEFAULT} />
          <Route element={<UserPage />} path={PATHS.PATH_USER_USER_DEFAULT} />
          <Route element={<UserPage />} path={PATHS.PATH_USER_USER_ACCOUNT} />
          <Route
            element={<UserPage />}
            path={PATHS.PATH_USER_USER_AUTHORIZATIONS}
          />
          <Route
            element={<UserPage />}
            path={PATHS.PATH_USER_USER_AUTHORIZATIONS_EDIT}
          />
          <Route element={<UserPage />} path={PATHS.PATH_USER_USER_SETTINGS} />
          <Route element={<UserPage />} path={PATHS.PATH_USER_USER_ADMIN} />
          <Route
            element={<UserPage />}
            path={PATHS.PATH_USER_USER_ADMIN_EDIT}
          />
          <Route element={<UserPage />} path={PATHS.PATH_USER_PERSON_DEFAULT} />
          <Route element={<UserPage />} path={PATHS.PATH_USER_PERSON_ADMIN} />
          <Route
            element={<UserPage />}
            path={PATHS.PATH_USER_PERSON_ADMIN_EDIT}
          />
          <Route
            element={<UserPage />}
            path={PATHS.PATH_USER_PERSON_ADDRESSES}
          />
          <Route
            element={<UserPage />}
            path={PATHS.PATH_USER_PERSON_ADDRESSES_EDIT}
          />
          <Route
            element={<UserPage />}
            path={PATHS.PATH_USER_PERSON_ADDRESSES_EDIT_NEW}
          />
          <Route
            element={<UserPage />}
            path={PATHS.PATH_USER_PERSON_ADDRESSES_EDIT_MODIFY}
          />
          <Route
            element={<UserPage />}
            path={PATHS.PATH_USER_PERSON_ADDRESSES_EDIT_DELETE}
          />
          <Route element={<UserPage />} path={PATHS.PATH_USER_PERSON_BANK} />
          <Route
            element={<UserPage />}
            path={PATHS.PATH_USER_PERSON_BANK_EDIT}
          />
          <Route element={<UserPage />} path={PATHS.PATH_USER_PERSON_BILLING} />
          <Route
            element={<UserPage />}
            path={PATHS.PATH_USER_PERSON_BILLING_EDIT}
          />
          <Route
            element={<UserPage />}
            path={PATHS.PATH_USER_PERSON_MEMBERSHIPS}
          />
          <Route
            element={<UserPage />}
            path={PATHS.PATH_USER_PERSON_MEMBERSHIPS_EDIT}
          />
          <Route element={<UserPage />} path={PATHS.PATH_USER_PERSON_CONTACT} />
          <Route
            element={<UserPage />}
            path={PATHS.PATH_USER_PERSON_CONTACT_EDIT}
          />
          <Route element={<UserPage />} path={PATHS.PATH_USER_PERSON_GENERAL} />
          <Route
            element={<UserPage />}
            path={PATHS.PATH_USER_PERSON_GENERAL_EDIT}
          />
          <Route
            element={<UserPage />}
            path={PATHS.PATH_USER_PERSON_PARTICIPATIONS}
          />
          <Route
            element={<UserPage />}
            path={PATHS.PATH_USER_PERSON_PENNYLANE}
          />
          <Route
            element={<UserPage />}
            path={PATHS.PATH_USER_PERSON_VERSIONS}
          />
        </Route>

        {/* ============================================ */}
        {/* My Account Pages                             */}
        {/* ============================================ */}
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_USER_ACCOUNT}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_USER_DEFAULT}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_DEFAULT}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_USER_AUTHORIZATIONS}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_USER_SETTINGS}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_GENERAL}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_GENERAL_EDIT}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_DEFAULT}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_ADDRESSES}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_ADDRESSES_EDIT}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_ADDRESSES_EDIT_NEW}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_ADDRESSES_EDIT_MODIFY}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_ADDRESSES_EDIT_DELETE}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_BANK}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_BANK_EDIT}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_BILLING}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_BILLING_EDIT}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_MEMBERSHIPS}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_MEMBERSHIPS_EDIT}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_CONTACT}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_CONTACT_EDIT}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_PARTICIPATIONS}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_PENNYLANE}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_VERSIONS}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_ADMIN}
        />
        <Route
          element={<MyAccountPage />}
          path={PATHS.PATH_MY_ACCOUNT_PERSON_ADMIN_EDIT}
        />

        {/* ============================================ */}
        {/* Master Data Pages                            */}
        {/* ============================================ */}
        <Route element={<CostsPage />} path={PATHS.PATH_COSTS} />
        <Route element={<NewCostPage />} path={PATHS.PATH_COSTS_NEW} />
        <Route element={<CostPage />} path={PATHS.PATH_COST_DEFAULT} />
        <Route element={<CostPage />} path={PATHS.PATH_COST_GENERAL} />
        <Route element={<CostPage />} path={PATHS.PATH_COST_GENERAL_EDIT} />
        <Route element={<CostPage />} path={PATHS.PATH_COST_VERSIONS} />
        <Route element={<CostPage />} path={PATHS.PATH_COST_ADMIN} />
        <Route element={<CostPage />} path={PATHS.PATH_COST_ADMIN_EDIT} />
        <Route element={<MaterialsPage />} path={PATHS.PATH_MATERIALS} />
        <Route element={<NewMaterialPage />} path={PATHS.PATH_MATERIALS_NEW} />
        <Route element={<MaterialPage />} path={PATHS.PATH_MATERIAL_DEFAULT} />
        <Route element={<MaterialPage />} path={PATHS.PATH_MATERIAL_GENERAL} />
        <Route
          element={<MaterialPage />}
          path={PATHS.PATH_MATERIAL_GENERAL_EDIT}
        />
        <Route element={<MaterialPage />} path={PATHS.PATH_MATERIAL_COSTS} />
        <Route
          element={<MaterialPage />}
          path={PATHS.PATH_MATERIAL_COSTS_EDIT}
        />
        <Route element={<MaterialPage />} path={PATHS.PATH_MATERIAL_VERSIONS} />
        <Route element={<MaterialPage />} path={PATHS.PATH_MATERIAL_ADMIN} />
        <Route
          element={<MaterialPage />}
          path={PATHS.PATH_MATERIAL_ADMIN_EDIT}
        />

        {/* ============================================ */}
        {/* Resolvable Services                          */}
        {/* ============================================ */}
        <Route
          element={<ResolvableServicesPage />}
          path={PATHS.PATH_RESOLVABLE_SERVICES}
        />
        <Route
          element={<NewResolvableServicePage />}
          path={PATHS.PATH_RESOLVABLE_SERVICES_NEW}
        />
        <Route
          element={<ResolvableServicePage />}
          path={PATHS.PATH_RESOLVABLE_SERVICE_DEFAULT}
        />
        <Route
          element={<ResolvableServicePage />}
          path={PATHS.PATH_RESOLVABLE_SERVICE_GENERAL}
        />
        <Route
          element={<ResolvableServicePage />}
          path={PATHS.PATH_RESOLVABLE_SERVICE_GENERAL_EDIT}
        />
        <Route
          element={<ResolvableServicePage />}
          path={PATHS.PATH_RESOLVABLE_SERVICE_QUOTATION_INFO}
        />
        <Route
          element={<ResolvableServicePage />}
          path={PATHS.PATH_RESOLVABLE_SERVICE_QUOTATION_INFO_EDIT}
        />
        <Route
          element={<ResolvableServicePage />}
          path={PATHS.PATH_RESOLVABLE_SERVICE_MATERIALS}
        />
        <Route
          element={<ResolvableServicePage />}
          path={PATHS.PATH_RESOLVABLE_SERVICE_MATERIALS_EDIT}
        />
        <Route
          element={<ResolvableServicePage />}
          path={PATHS.PATH_RESOLVABLE_SERVICE_COSTS}
        />
        <Route
          element={<ResolvableServicePage />}
          path={PATHS.PATH_RESOLVABLE_SERVICE_COSTS_EDIT}
        />
        <Route
          element={<ResolvableServicePage />}
          path={PATHS.PATH_RESOLVABLE_SERVICE_VERSIONS}
        />
        <Route
          element={<ResolvableServicePage />}
          path={PATHS.PATH_RESOLVABLE_SERVICE_ADMIN}
        />
        <Route
          element={<ResolvableServicePage />}
          path={PATHS.PATH_RESOLVABLE_SERVICE_ADMIN_EDIT}
        />

        {/* ============================================ */}
        {/* Resolvable Mission Types                     */}
        {/* ============================================ */}
        <Route
          element={<ResolvableMissionTypesPage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPES}
        />
        <Route
          element={<NewResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPES_NEW}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_DEFAULT}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_GENERAL}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_GENERAL_EDIT}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_TEMPLATES}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_TEMPLATES_EDIT}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_QUOTATION_HEADER}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_QUOTATION_HEADER_EDIT}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT_DELETE}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT_MODIFY}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT_NEW}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={
            PATHS.PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT_NEW_IN_GROUP
          }
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT_REORDER}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_FILES}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_FILES_EDIT}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_TOOLKITS}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_TOOLKITS_EDIT}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_VERSIONS}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_ADMIN}
        />
        <Route
          element={<ResolvableMissionTypePage />}
          path={PATHS.PATH_RESOLVABLE_MISSION_TYPE_ADMIN_EDIT}
        />

        {/* ============================================ */}
        {/* Document & Section Templates                 */}
        {/* ============================================ */}
        <Route element={<DummyFolderContextProvider />}>
          <Route element={<DummyMissionContextProvider />}>
            <Route
              element={<DocumentAndSectionTemplatesPage />}
              path={PATHS.PATH_DOCUMENT_TEMPLATES}
            />
            <Route
              element={<DocumentAndSectionTemplatesPage />}
              path={PATHS.PATH_DOCUMENT_AND_SECTION_TEMPLATES_DEFAULT}
            />
            <Route
              element={<NewDocumentTemplatePage />}
              path={PATHS.PATH_DOCUMENT_TEMPLATES_NEW_INFO}
            />
            <Route
              element={<NewDocumentTemplatePage />}
              path={PATHS.PATH_DOCUMENT_TEMPLATES_NEW_INFO_EDIT}
            />
            <Route
              element={<NewDocumentTemplatePage />}
              path={PATHS.PATH_DOCUMENT_TEMPLATES_NEW_DEFAULT}
            />
            <Route
              element={<NewDocumentTemplatePage />}
              path={PATHS.PATH_DOCUMENT_TEMPLATES_NEW_EDITOR}
            />
            <Route
              element={<NewDocumentTemplatePage />}
              path={PATHS.PATH_DOCUMENT_TEMPLATES_NEW_SUMMARY}
            />
            <Route
              element={<DocumentTemplatePage />}
              path={PATHS.PATH_DOCUMENT_TEMPLATE_INFO}
            />
            <Route
              element={<DocumentTemplatePage />}
              path={PATHS.PATH_DOCUMENT_TEMPLATE_INFO_EDIT}
            />
            <Route
              element={<DocumentTemplatePage />}
              path={PATHS.PATH_DOCUMENT_TEMPLATE_EDITOR}
            />
            <Route
              element={<DocumentTemplatePage />}
              path={PATHS.PATH_DOCUMENT_TEMPLATE_DEFAULT}
            />
            <Route
              element={<DocumentTemplatePage />}
              path={PATHS.PATH_DOCUMENT_TEMPLATE_SUMMARY}
            />
            <Route
              element={<DocumentTemplatePage />}
              path={PATHS.PATH_DOCUMENT_TEMPLATE_PREVIEW}
            />
            <Route
              element={<DocumentTemplatePage />}
              path={PATHS.PATH_DOCUMENT_TEMPLATE_GENERATION}
            />
            <Route
              element={<DocumentTemplatePage />}
              path={PATHS.PATH_DOCUMENT_TEMPLATE_VERSIONS}
            />
            <Route
              element={<DocumentTemplatePage />}
              path={PATHS.PATH_DOCUMENT_TEMPLATE_ADMIN}
            />
            <Route
              element={<DocumentTemplatePage />}
              path={PATHS.PATH_DOCUMENT_TEMPLATE_ADMIN_EDIT}
            />
            <Route
              element={<DocumentAndSectionTemplatesPage />}
              path={PATHS.PATH_SECTION_TEMPLATES}
            />
            <Route
              element={<NewSectionTemplatePage />}
              path={PATHS.PATH_SECTION_TEMPLATES_NEW_INFO}
            />
            <Route
              element={<NewSectionTemplatePage />}
              path={PATHS.PATH_SECTION_TEMPLATES_NEW_INFO_EDIT}
            />
            <Route
              element={<NewSectionTemplatePage />}
              path={PATHS.PATH_SECTION_TEMPLATES_NEW_DEFAULT}
            />
            <Route
              element={<NewSectionTemplatePage />}
              path={PATHS.PATH_SECTION_TEMPLATES_NEW_EDITOR}
            />
            <Route
              element={<NewSectionTemplatePage />}
              path={PATHS.PATH_SECTION_TEMPLATES_NEW_SUMMARY}
            />
            <Route
              element={<SectionTemplatePage />}
              path={PATHS.PATH_SECTION_TEMPLATE_INFO}
            />
            <Route
              element={<SectionTemplatePage />}
              path={PATHS.PATH_SECTION_TEMPLATE_INFO_EDIT}
            />
            <Route
              element={<SectionTemplatePage />}
              path={PATHS.PATH_SECTION_TEMPLATE_EDITOR}
            />
            <Route
              element={<SectionTemplatePage />}
              path={PATHS.PATH_SECTION_TEMPLATE_DEFAULT}
            />
            <Route
              element={<SectionTemplatePage />}
              path={PATHS.PATH_SECTION_TEMPLATE_SUMMARY}
            />
            <Route
              element={<SectionTemplatePage />}
              path={PATHS.PATH_SECTION_TEMPLATE_PREVIEW}
            />
            <Route
              element={<SectionTemplatePage />}
              path={PATHS.PATH_SECTION_TEMPLATE_VERSIONS}
            />
            <Route
              element={<SectionTemplatePage />}
              path={PATHS.PATH_SECTION_TEMPLATE_ADMIN}
            />
            <Route
              element={<SectionTemplatePage />}
              path={PATHS.PATH_SECTION_TEMPLATE_ADMIN_EDIT}
            />
          </Route>
        </Route>
      </Route>

      {/* ============================================ */}
      {/* Print Mode Routes                            */}
      {/* ============================================ */}
      <Route element={<LoggedInLayout isInPrintMode />}>
        <Route element={<DummyFolderContextProvider />}>
          <Route element={<DummyMissionContextProvider isInPrintMode />}>
            <Route
              element={<NewDocumentTemplatePage isInPrintMode />}
              path={PATHS.PATH_DOCUMENT_TEMPLATES_NEW_PRINTER}
            />
            <Route
              element={<DocumentTemplatePage isInPrintMode />}
              path={PATHS.PATH_DOCUMENT_TEMPLATE_PRINTER}
            />
            <Route
              element={<NewSectionTemplatePage isInPrintMode />}
              path={PATHS.PATH_SECTION_TEMPLATES_NEW_PRINTER}
            />
            <Route
              element={<SectionTemplatePage isInPrintMode />}
              path={PATHS.PATH_SECTION_TEMPLATE_PRINTER}
            />
          </Route>
        </Route>

        {/* Mission Report Print Mode */}
        <Route element={<FolderContextProvider page={Page.MissionReport} />}>
          {withSubMissionRoutes(
            <Route element={<MissionContextProvider isInPrintMode />}>
              <Route element={<ReportContextProvider />}>
                <Route
                  element={<MissionReportPage isInPrintMode />}
                  path={PATHS.PATH_MISSION_REPORT_PRINTER}
                />
              </Route>
            </Route>,
          )}
        </Route>

        {/* Mission Quotation Print Mode */}
        <Route element={<FolderContextProvider page={Page.MissionQuotation} />}>
          {withSubMissionRoutes(
            <Route element={<MissionContextProvider isInPrintMode />}>
              <Route element={<QuotationContextProvider />}>
                <Route
                  element={<MissionQuotationPage isInPrintMode />}
                  path={PATHS.PATH_MISSION_QUOTATION_PRINTER}
                />
              </Route>
            </Route>,
          )}
        </Route>
      </Route>

      {/* ============================================ */}
      {/* Logged Out Routes                            */}
      {/* ============================================ */}
      <Route element={<LoggedOutLayout />}>
        <Route element={<LoginPage />} path={PATHS.PATH_LOGIN} />
        <Route element={<HealthCheckPage />} path={PATHS.PATH_HEALTH_CHECK} />
        <Route element={<NotFoundPage />} path={PATHS.PATH_NOT_FOUND} />
        <Route element={<NotFoundPage />} path="*" />
      </Route>
    </>,
  ),
);
